import React, { ReactNode } from 'react';
import { Text } from 'shared-components';

// Minimal shape that matches shared-components' FaqProps without coupling to a
// path that isn't re-exported from the barrel. Kept compatible so we can pass
// these objects straight into <Faq /> via Container.tsx.
export type FaqQuestion = {
  question?: string | ReactNode;
  answer?: string | ReactNode;
};

export type FaqProps = {
  heading?: string;
  questions?: FaqQuestion[];
};

// Single source of truth for the loan-flow FAQ content. Used by both:
//   - ContentCard.tsx (legacy chrome, for routes still rendered there: /, /app/done)
//   - LoanWizardShell.tsx (new shell, via the `faq` prop on each migrated step)
//
// Keep questions grouped by route so adding/removing a route's FAQ stays a
// single-line change.

const HEADING = 'Frequently Asked Questions';

const HOME_QUESTIONS: FaqQuestion[] = [
  {
    question: 'How does Tento work?',
    answer:
      'With Tento, it’s never been easier to access capital. We want to help you solve your short and medium-term capital needs, so you can keep building a successful, thriving business in the long-term. That’s why we reward healthy businesses with savings and lower APRs. We offer both working capital and equipment financing based on the health of your business.',
  },
  {
    question: 'Is Tento right for my business?',
    answer: `Tento is here to help businesses of all sizes and needs. When reviewing your business, our goal is to help get you the best possible offer. The best way to see what Tento can do for your business is to go through our simple application. There's no cost to apply, no obligation to use your offer once approved and no impact to your credit score.`,
  },
  {
    question: 'How does the application work?',
    answer: `There are no hard credit checks, lengthy paperwork, or required personal financial statements. All you have to do is connect your bank information using Plaid or provide 4 months of bank statements through our secure portal, tell us a few details about yourself and your business and you’ll receive your offer within hours.`,
  },
];

const AMOUNT_QUESTIONS: FaqQuestion[] = [
  {
    question: 'How much capital can I access?',
    answer: 'Tento provides funding from $10K - $500K for (almost) everything your business needs it for.',
  },
  {
    question: 'How do you calculate my offer?',
    answer: `We take a holistic view of your business using your bank data, reviewing the overall health of your business and historical performance. Other factors we consider when reviewing bank data are average daily balance, total credits, total debits, competitor balances and exposure among others. When you connect your bank data, we will quickly generate your initial offer details.`,
  },
  {
    question: 'Does it cost anything to sign up?',
    answer: `No! It costs nothing to join Tento and there is absolutely no obligation to accept an offer.  Applying takes just a few minutes. Securely connect your bank, upload your ID and enter a few personal details to find out how much capital you can access.`,
  },
];

const FURTHER_DETAILS_QUESTIONS: FaqQuestion[] = [
  {
    question: 'Where do I find Federal Tax ID?',
    answer: (
      <>
        <Text variant="muted" id="find-federal-tax-id-title">
          If you lost or misplaced your EIN, you can retrieve it in the following ways:
        </Text>
        <p>
          <Text variant="muted" id="find-federal-tax-id-option1">
            · Reference the original notice issued by the IRS when you received your EIN or call the IRS Business &amp;
            Specialty Tax Line at 1-800-829-4933.
          </Text>
        </p>
        <p>
          <Text variant="muted" id="find-federal-tax-id-option2">
            · If you used it to open a bank account or get a license, contact these organizations they should be able to
            retrieve your number.
          </Text>
        </p>
        <p>
          <Text variant="muted" id="find-federal-tax-id-option3">
            · Find an old tax return. Your EIN should be on it.
          </Text>
        </p>
      </>
    ),
  },
  {
    question: 'What is my “Gross Revenue”?',
    answer: 'Gross Revenue is the total sales for your business before any expenses.',
  },
];

const BANK_CONNECT_QUESTIONS: FaqQuestion[] = [
  {
    question: 'Is my information secure?',
    answer:
      'Yes. We take our privacy policy very seriously and never share your information. To learn more about how we store your information securely, you can read our privacy policy here.',
  },
  {
    question: 'Why do I need to connect Plaid?',
    answer: `A more complete picture of your business will result in better offers and more capital available for your business. Tento assesses your risk level based on secure live data connections. More complete data connections typically mean better ratings and more capital available for your business.`,
  },
  {
    question: 'Is  my credit score impacted?',
    answer: `No! We’re here to help you build better business credit. The goal is that by using our products, our financing will enhance your credit history with positive payment history.`,
  },
];

const OWNERS_QUESTIONS: FaqQuestion[] = [
  {
    question: 'Is my information secure?',
    answer: (
      <>
        Yes. We take our privacy policy very seriously. To learn more about how we store your information securely, you
        can read our privacy policy{' '}
        <a href="https://www.tento.com/privacy" target="_blank" rel="noreferrer">
          here
        </a>
        .
      </>
    ),
  },
  {
    question: 'Why do you need my Social Security Number?',
    answer:
      'We collect your social security number to verify your identity and determine eligibility for our financing products.',
  },
  {
    question: `Will Tento's financing report to my personal credit bureau?`,
    answer: `No, we are a commercial lender and therefore report your payment history to applicable business credit bureaus.`,
  },
  {
    question: 'Should I add additional owners?',
    answer:
      'If you are the only owner in the business, then there is no need to add anyone else.  If you have a partner(s) then you will also need to provide their information as well.',
  },
];

const FUNDING_IS_ALMOST_HERE_QUESTIONS: FaqQuestion[] = [
  {
    question: 'How long will it take to receive my offer?',
    answer:
      'A decision on your financing request typically takes a few hours from the time you submit your application.  In some instances it could take 1-2 business days.',
  },
  {
    question: 'What if the information shown is incorrect?',
    answer: 'No problem, mistakes happen. You can go back into any step of the application to fix errors.',
  },
  {
    question: 'What if I have multiple bank accounts?',
    answer: 'We’d recommend uploading all business bank accounts so we have an accurate picture of your cash flow.',
  },
];

const DONE_QUESTIONS: FaqQuestion[] = [
  {
    question: 'How fast will I receive my capital?',
    answer:
      'Once approved and we receive your signed documentation, your business will typically receive funding within 1 to 2 business days. We know your business needs funding now - not later.',
  },
  {
    question: 'Do I need to do anything else?',
    answer: `Not right now! But feel free to explore more of our financial wellness products below.`,
  },
];

const ROUTE_TO_QUESTIONS: Record<string, FaqQuestion[]> = {
  '/': HOME_QUESTIONS,
  '/app/amount': AMOUNT_QUESTIONS,
  '/app/business/further-details': FURTHER_DETAILS_QUESTIONS,
  '/app/bank-connect': BANK_CONNECT_QUESTIONS,
  '/app/business/owners': OWNERS_QUESTIONS,
  '/app/funding-is-almost-here': FUNDING_IS_ALMOST_HERE_QUESTIONS,
  '/app/done': DONE_QUESTIONS,
};

export const getLoanFaqForRoute = (pathname?: string): FaqProps | undefined => {
  if (!pathname) return undefined;
  const questions = ROUTE_TO_QUESTIONS[pathname];
  if (!questions) return undefined;
  return { heading: HEADING, questions };
};
